import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { RankingChartLabels } from '@digital-twins/deck-ui/components'
import { OtherToggleLabels, ShareUnit, ShareUnitToggleLabels } from '@digital-twins/deck-ui/plots/_shared'
import { StudyFigureGroup, StudyFigureQuestion } from '@digital-twins/shared-types'

/**
 * Shared pieces of the survey tab's figure adapters (plots plan D3). Every
 * adapter is shape only: it prints figures Python rounded, at the decimals
 * Python stamped, in the order Python sent. Layout (bar widths, positions) is
 * the only thing derived here.
 */

/** One drawn group's plot: Total alone, or one cut group. */
export interface FigurePanel {
  group: StudyFigureGroup
  plot: { kind: string; data: Record<string, unknown> }
}

export interface FigureViewProps {
  question: StudyFigureQuestion
  /** Total alone when no cut is active, else the cut's drawn groups in cut order. */
  panels: Array<FigurePanel>
  /** True when the panels are cut groups (the group names are drawn). */
  isCut: boolean
  /** The card's chart palette (today's `appChartColors`). */
  colors: Array<string>
  /** The viewer's UI locale for number formatting. */
  locale: string
  /** Today's ranking chrome (seam I): the card's translated ranking labels. */
  rankingLabels?: Partial<RankingChartLabels>
  /** The "Other" link's words in the viewer's UI language (plots plan OT); no link without them. */
  otherToggleLabels?: OtherToggleLabels
  /**
   * A payload stored before its plot was stamped (an analyst chart read by a
   * legacy adapter): its names keep today's display recasing. Stamped names
   * print as Python wrote them (Wave 7 S4).
   */
  isLegacy?: boolean
  /**
   * The number the card header already prints. A likert row hides its base
   * line only when every row's base is this number and that row excluded nobody.
   */
  visibleDenominator?: number | null
  /** The unit a view with a percent / count toggle opens on (a room chart opens on counts). Default percent. */
  initialShareUnit?: ShareUnit
}

/** A figure Python already rounded, printed at exactly its stamped decimals ("4.0" stays "4.0"). */
export function formatStampedNumber(value: number, decimals: number, locale: string): string {
  const digits = Math.max(0, decimals)
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value)
}

export function formatStampedShare(value: number, decimals: number, locale: string): string {
  return `${formatStampedNumber(value, decimals, locale)}%`
}

/** Fills a `deck_labels.py` chrome template (`{n}`, `{low}`, ...). */
export function fillPlotLabel(template: string | undefined, params: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    params[key] === undefined ? match : String(params[key]),
  )
}

/** The neutral "Other" colour (plan Rule defaults: Other is always neutral). */
export const OTHER_COLOR = 'var(--slide-text-muted)'

interface FigurePanelHeadingProps {
  group: StudyFigureGroup
  lowBaseLabel?: string
  testId: string
}

/** A cut group's name, with Python's low-base flag when it is set. */
export function FigurePanelHeading(props: FigurePanelHeadingProps) {
  const { group, lowBaseLabel, testId } = props

  const lowBase = group.lowBase && lowBaseLabel ? (
    <span className="text-xs font-normal text-muted-foreground" data-testid={`${testId}-low-base`}>
      {lowBaseLabel}
    </span>
  ) : null

  return (
    <div className="flex items-baseline gap-2 text-sm font-semibold text-foreground" data-testid={testId}>
      <span>{group.label ?? ''}</span>
      {lowBase}
    </div>
  )
}

interface FigurePanelSectionsProps {
  panels: Array<FigurePanel>
  isCut: boolean
  testIdPrefix: string
  lowBaseLabel?: (panel: FigurePanel) => string | undefined
  renderPanel: (panel: FigurePanel, index: number) => ReactNode
}

/** Total drawn alone; a cut drawn as one titled section per group. */
export function FigurePanelSections(props: FigurePanelSectionsProps) {
  const { panels, isCut, testIdPrefix, lowBaseLabel, renderPanel } = props

  if (!isCut) {
    return <>{panels.map((panel, index) => <div key={panel.group.id}>{renderPanel(panel, index)}</div>)}</>
  }
  return (
    <div className="space-y-8">
      {panels.map((panel, index) => (
        <section key={panel.group.id} className="space-y-3" data-testid={`${testIdPrefix}-group-${index}`}>
          <FigurePanelHeading
            group={panel.group}
            lowBaseLabel={lowBaseLabel?.(panel)}
            testId={`${testIdPrefix}-group-heading-${index}`}
          />
          {renderPanel(panel, index)}
        </section>
      ))}
    </div>
  )
}

/** The # / % switch's words in the viewer's language (one switch per figure card). */
export function useShareUnitLabels(): ShareUnitToggleLabels {
  const { t } = useTranslation('app')

  return {
    group: t('studyReport.plotToggle.shareUnit'),
    percent: t('studyReport.plotToggle.percent'),
    count: t('studyReport.plotToggle.count'),
  }
}
